import type {
    ConfigDiagnostic,
    ConfigOwner,
    ConfigTarget,
    SettingsSnapshot,
} from "@magic-md/engine-client/config";
import { useT } from "@magic-md/i18n/react";
import {
    FolderOpen,
    Loader2,
    RefreshCw,
    SlidersHorizontal,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { getPlatformService } from "../../../platform";
import { useEditorStore } from "../../../store/editor-store";
import type { SettingsSection } from "../../../store/settings-store";
import { getThemeAccentStyle } from "../../../theme/runtime/theme-accents";
import { SECTION_ICONS, SECTION_LABELS } from "./constants";
import { useSettingsTextResolver } from "./localization";
import { collectLayerPaths, layerContextLabel, targetLabel } from "./resolvers";

export function LayerHeader({
    target,
    section,
    sectionIcon,
    sectionLabel,
    owners,
    snapshot,
    loading,
    reloading,
    onReload,
}: {
    target: ConfigTarget;
    section: SettingsSection;
    sectionIcon?: React.ReactNode;
    sectionLabel?: string;
    owners: ConfigOwner[];
    snapshot: SettingsSnapshot | null;
    loading: boolean;
    reloading: boolean;
    onReload: () => Promise<void>;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const paths = collectLayerPaths(snapshot, target, owners);
    const contextLabel = layerContextLabel(target, owners, resolveText);

    return (
        <div className="shrink-0 border-b border-border bg-background px-3 py-2">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border/70 bg-muted/30 text-muted-foreground">
                            {sectionIcon ?? SECTION_ICONS[section]}
                        </span>
                        <span className="text-sm font-medium text-foreground">
                            {sectionLabel ?? resolveText(SECTION_LABELS[section])}
                        </span>
                        <HeaderBadge>{targetLabel(target, resolveText)}</HeaderBadge>
                        {paths.length > 0 ? (
                            paths.map((item) => (
                                <PathMetaButton
                                    key={`${item.label}:${item.path}`}
                                    label={item.label}
                                    path={item.path}
                                />
                            ))
                        ) : (
                            <HeaderMeta title={t("settings.layer.noWritableFile")}>
                                {t("settings.layer.noConfigFile")}
                            </HeaderMeta>
                        )}
                        <HeaderMeta title={t("settings.layer.contextTitle")}>
                            {contextLabel}
                        </HeaderMeta>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    {loading ? (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Loader2 size={12} className="animate-spin" />
                        </span>
                    ) : null}
                    <button
                        type="button"
                        aria-label={t("settings.reload")}
                        title={t("settings.reload")}
                        onClick={() => {
                            void onReload();
                        }}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                        <RefreshCw size={13} className={cn(reloading && "animate-spin")} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function HeaderBadge({ children }: { children: React.ReactNode }) {
    return (
        <span className="inline-flex h-6 items-center rounded-md border border-primary/20 bg-primary/10 px-2 text-[11px] font-medium text-primary">
            {children}
        </span>
    );
}

function HeaderMeta({
    children,
    title,
}: {
    children: React.ReactNode;
    title?: string;
}) {
    return (
        <span
            className="inline-flex h-6 items-center rounded-md border border-border/70 bg-muted/25 px-2 text-[11px] text-muted-foreground"
            title={title}
        >
            {children}
        </span>
    );
}

function PathMetaButton({ label, path }: { label: string; path: string }) {
    return (
        <button
            type="button"
            onClick={() => {
                void openConfigPath(path);
            }}
            className="inline-flex h-6 max-w-full items-center gap-1 rounded-md border border-border/70 bg-muted/25 px-2 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title={path}
        >
            <FolderOpen size={12} />
            <span className="truncate">{label}</span>
        </button>
    );
}

export function EmptyState({
    message,
    loading = false,
}: {
    message: string;
    loading?: boolean;
}) {
    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
            {loading ? (
                <Loader2 size={16} className="animate-spin" />
            ) : (
                <SlidersHorizontal size={18} />
            )}
            <div>{message}</div>
        </div>
    );
}

export function DiagnosticsBanner({
    diagnostics,
    repairingIds = [],
    onRepairDiagnostic,
}: {
    diagnostics: SettingsSnapshot["diagnostics"];
    repairingIds?: string[];
    onRepairDiagnostic?: (diagnostic: ConfigDiagnostic) => void | Promise<void>;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const errors = diagnostics.filter((item) => item.severity === "error");
    const warnings = diagnostics.filter((item) => item.severity === "warning");

    return (
        <section
            className="theme-accent-banner rounded-lg px-4 py-3"
            style={getThemeAccentStyle("warning")}
        >
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{t("settings.diagnostics.title")}</span>
                {errors.length > 0 ? (
                    <Badge>{t("settings.diagnostics.errors", { count: errors.length })}</Badge>
                ) : null}
                {warnings.length > 0 ? (
                    <Badge>
                        {t("settings.diagnostics.warnings", { count: warnings.length })}
                    </Badge>
                ) : null}
            </div>
            <div className="mt-3 space-y-2">
                {diagnostics.map((diagnostic, index) => {
                    const repairTarget = getDiagnosticRepairTarget(diagnostic);
                    const repairId = getDiagnosticRepairId(diagnostic);
                    const repairing = repairId
                        ? repairingIds.includes(repairId)
                        : false;

                    return (
                        <div
                            key={`${diagnostic.code}:${diagnostic.key ?? diagnostic.filePath ?? index}`}
                            className="theme-accent-border rounded-md border bg-background/70 px-3 py-2 text-xs text-muted-foreground"
                            style={getThemeAccentStyle("warning")}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 font-medium text-foreground">
                                    {resolveText(diagnostic.message, diagnostic.message)}
                                </div>
                                {repairTarget && onRepairDiagnostic ? (
                                    <button
                                        type="button"
                                        disabled={repairing}
                                        onClick={() => {
                                            void onRepairDiagnostic(diagnostic);
                                        }}
                                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                                        title={t("settings.diagnostics.removeFromTarget", {
                                            target: targetLabel(repairTarget.target, resolveText),
                                        })}
                                    >
                                        {repairing ? (
                                            <Loader2
                                                size={11}
                                                className="animate-spin text-muted-foreground"
                                            />
                                        ) : null}
                                        {t("settings.diagnostics.removeFromTarget", {
                                            target: targetLabel(repairTarget.target, resolveText),
                                        })}
                                    </button>
                                ) : null}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-2">
                                <Badge>{diagnostic.severity}</Badge>
                                <Badge>{diagnostic.code}</Badge>
                                {diagnostic.key ? <Badge>{diagnostic.key}</Badge> : null}
                                {diagnostic.filePath ? (
                                    <Badge>{diagnostic.filePath}</Badge>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

const REPAIRABLE_DIAGNOSTIC_CODES = new Set([
    "config.invalidType",
    "config.remoteNotAllowed",
    "config.unknownKey",
    "config.workspaceNotAllowed",
]);

export function getDiagnosticRepairId(
    diagnostic: ConfigDiagnostic,
): string | null {
    const target = getDiagnosticRepairTarget(diagnostic);
    return target ? `${target.owner}:${target.target}:${target.key}` : null;
}

function getDiagnosticRepairTarget(
    diagnostic: ConfigDiagnostic,
): {
    owner: ConfigOwner;
    target: ConfigTarget;
    key: string;
} | null {
    if (!REPAIRABLE_DIAGNOSTIC_CODES.has(diagnostic.code)) return null;
    if (!diagnostic.key || !diagnostic.owner) return null;
    if (
        diagnostic.target !== "user" &&
        diagnostic.target !== "workspace" &&
        diagnostic.target !== "remoteHost"
    ) {
        return null;
    }
    return {
        owner: diagnostic.owner,
        target: diagnostic.target,
        key: diagnostic.key,
    };
}

export function MiniBadge({ children }: { children: React.ReactNode }) {
    return (
        <span className="rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground">
            {children}
        </span>
    );
}

export function InfoChip({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-md border border-border/80 bg-muted/10 px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {label}
            </div>
            <div className="mt-1 text-xs text-foreground">{value}</div>
        </div>
    );
}

export function Badge({ children }: { children: React.ReactNode }) {
    return (
        <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5">
            {children}
        </span>
    );
}

export function DetailModeSwitch({
    mode,
    onChange,
}: {
    mode: "form" | "info";
    onChange: (mode: "form" | "info") => void;
}) {
    const t = useT("editor");
    return (
        <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5">
            {[
                { id: "form", label: t("settings.detailMode.form") },
                { id: "info", label: t("settings.detailMode.info") },
            ].map((item) => (
                <button
                    key={item.id}
                    type="button"
                    onClick={() => onChange(item.id as "form" | "info")}
                    className={cn(
                        "rounded px-2.5 py-1 text-[11px] transition-colors",
                        mode === item.id
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                    )}
                >
                    {item.label}
                </button>
            ))}
        </div>
    );
}

async function openConfigPath(path: string): Promise<void> {
    try {
        await useEditorStore.getState().openFileFromPath(path);
    } catch {
        await getPlatformService().openExternal?.(path);
    }
}
